import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Seo from "../components/Seo";
import { MediaPicker } from "../components/MediaPicker";
import { adminCreateArticle, adminGetArticle, adminMe, adminUpdateArticle, fetchSettings } from "../lib/api";
import { AdminHeader } from "../components/AdminNav";

const slugify = (s) =>
    (s || "")
        .toLowerCase()
        .replace(/[^\p{L}\p{N}\s-]/gu, "")
        .trim()
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-");

const todayISO = () => new Date().toISOString().slice(0, 10);

const blank = {
    slug: "",
    title: "",
    deck: "",
    body: "",
    featured_image: "",
    gallery: [],
    author: "ASC Interior",
    category: "",
    read_time: "",
    publish_date: todayISO(),
    publish_at: "",
    status: "draft",
    seo_title: "",
    seo_description: "",
    og_image: "",
};

export default function AdminArticleEditor() {
    const { slug: editSlug } = useParams();
    const isEdit = Boolean(editSlug);
    const navigate = useNavigate();
    const [form, setForm] = useState(blank);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        adminMe().catch(() => navigate("/admin/login", { replace: true }));
    }, [navigate]);

    useEffect(() => {
        fetchSettings()
            .then((s) => setCategories(s?.article_categories || []))
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (!isEdit) return;
        adminGetArticle(editSlug)
            .then((a) => setForm({ ...blank, ...a, gallery: a.gallery || [] }))
            .catch((err) => setError(err?.response?.data?.detail || "Could not load article"))
            .finally(() => setLoading(false));
    }, [isEdit, editSlug]);

    const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

    const addGallery = () => setForm((f) => ({ ...f, gallery: [...(f.gallery || []), ""] }));
    const setGallery = (i, url) =>
        setForm((f) => {
            const next = [...(f.gallery || [])];
            next[i] = url;
            return { ...f, gallery: next };
        });
    const removeGallery = (i) =>
        setForm((f) => ({ ...f, gallery: (f.gallery || []).filter((_, idx) => idx !== i) }));

    const deckWordCount = useMemo(
        () => (form.deck || "").trim().split(/\s+/).filter(Boolean).length,
        [form.deck]
    );

    const submit = async (statusOverride) => {
        setError("");
        setSaving(true);
        try {
            const payload = {
                ...form,
                slug: form.slug || slugify(form.title),
                gallery: (form.gallery || []).filter(Boolean),
                status: statusOverride || form.status,
                publish_at: form.status === "scheduled" ? form.publish_at || null : null,
            };
            if (isEdit) {
                await adminUpdateArticle(editSlug, payload);
            } else {
                await adminCreateArticle(payload);
            }
            navigate("/admin/articles");
        } catch (err) {
            setError(err?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <main className="min-h-screen bg-[#F9F8F6] px-6 md:px-12 lg:px-24 py-16">
                <p className="asc-overline">Loading…</p>
            </main>
        );
    }

    return (
        <main
            data-testid="page-admin-article-editor"
            className="min-h-screen bg-[#F9F8F6] px-6 md:px-12 lg:px-24 py-16 md:py-24"
        >
            <Seo
                path={isEdit ? `/admin/articles/${editSlug}/edit` : "/admin/articles/new"}
                title={isEdit ? "Edit article" : "New article"}
                description="Studio Life editor"
                noindex
            />
            <AdminHeader title={isEdit ? "Edit Studio Life entry" : "New Studio Life entry"} />

            {error && <p className="mt-6 text-red-700 text-sm" data-testid="article-editor-error">{error}</p>}

            <div className="mt-6 border-l-2 border-[#E5E1DB] pl-4 max-w-3xl">
                <p className="asc-overline text-[#8C7A6B]">Editorial rules</p>
                <ul className="mt-2 text-sm text-[#5C5855] leading-relaxed list-disc pl-5">
                    <li>Titles use Title Case, no trailing period, avoid marketing language.</li>
                    <li>Deck: max ~25 words, sentence case, no trailing period, no promotional adjectives.</li>
                    <li>Publish date format is standardised site-wide (e.g. 9 July 2026).</li>
                </ul>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="mt-10 max-w-3xl space-y-8">
                <Section title="Identity">
                    <Field label="Title" required>
                        <input
                            data-testid="art-title"
                            value={form.title}
                            onChange={(e) => {
                                set("title", e.target.value);
                                if (!isEdit) set("slug", slugify(e.target.value));
                            }}
                            required
                            className={inputCls}
                        />
                    </Field>
                    <Field label="Slug" hint="URL: /studio-life/[slug]">
                        <input
                            data-testid="art-slug"
                            value={form.slug}
                            onChange={(e) => set("slug", slugify(e.target.value))}
                            required
                            className={inputCls}
                        />
                    </Field>
                    <Field
                        label={`Deck / short summary (${deckWordCount} words)`}
                        hint="Max ~25 words · sentence case · no trailing period"
                    >
                        <textarea data-testid="art-deck" rows={2} value={form.deck} onChange={(e) => set("deck", e.target.value)} className={inputCls} />
                    </Field>
                </Section>

                <Section title="Metadata">
                    <Field label="Category" required>
                        <select
                            data-testid="art-category"
                            value={form.category}
                            onChange={(e) => set("category", e.target.value)}
                            required
                            className={inputCls}
                        >
                            <option value="">Select category…</option>
                            {categories.map((c) => (
                                <option key={c} value={c}>{c}</option>
                            ))}
                        </select>
                    </Field>
                    <Field label="Author">
                        <input data-testid="art-author" value={form.author} onChange={(e) => set("author", e.target.value)} className={inputCls} />
                    </Field>
                    <Field label="Read time" hint="e.g. '6 min read' — hidden for STUDIO LIFE and PRESS & AWARDS categories">
                        <input data-testid="art-read-time" value={form.read_time} onChange={(e) => set("read_time", e.target.value)} className={inputCls} />
                    </Field>
                    <Field label="Publish date">
                        <input
                            data-testid="art-publish-date"
                            type="date"
                            value={form.publish_date}
                            onChange={(e) => set("publish_date", e.target.value)}
                            className={inputCls}
                        />
                    </Field>
                </Section>

                <Section title="Body">
                    <Field label="Body content" hint="Use blank lines for paragraphs">
                        <textarea data-testid="art-body" rows={16} value={form.body} onChange={(e) => set("body", e.target.value)} className={inputCls} />
                    </Field>
                </Section>

                <Section title="Imagery">
                    <MediaPicker
                        label="Featured image"
                        testid="art-featured"
                        value={form.featured_image}
                        onChange={(url) => set("featured_image", url)}
                    />
                    <div>
                        <p className="text-[10px] uppercase tracking-[0.22em] text-[#8C7A6B] mb-2">Gallery images</p>
                        <div className="space-y-4">
                            {(form.gallery || []).map((g, i) => (
                                <div key={i} className="flex items-start gap-3">
                                    <div className="flex-1">
                                        <MediaPicker
                                            label={`Gallery image ${i + 1}`}
                                            testid={`art-gallery-${i}`}
                                            value={g}
                                            onChange={(url) => setGallery(i, url)}
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => removeGallery(i)}
                                        data-testid={`art-gallery-remove-${i}`}
                                        className="mt-6 text-[10px] uppercase tracking-[0.18em] text-red-700"
                                    >
                                        Remove
                                    </button>
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={addGallery}
                                data-testid="art-gallery-add"
                                className="px-4 py-2 text-[11px] uppercase tracking-[0.22em] border border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#F9F8F6]"
                            >
                                + Add gallery image
                            </button>
                        </div>
                    </div>
                </Section>

                <Section title="SEO">
                    <Field label="SEO title" hint="Falls back to the entry title if blank">
                        <input data-testid="art-seo-title" value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} className={inputCls} />
                    </Field>
                    <Field label="SEO description" hint="Falls back to the deck if blank">
                        <textarea data-testid="art-seo-desc" rows={3} value={form.seo_description} onChange={(e) => set("seo_description", e.target.value)} className={inputCls} />
                    </Field>
                    <MediaPicker
                        label="Open Graph image (social share)"
                        testid="art-og"
                        value={form.og_image}
                        onChange={(url) => set("og_image", url)}
                    />
                </Section>

                <Section title="Publish">
                    <Field label="Status">
                        <select
                            data-testid="art-status"
                            value={form.status}
                            onChange={(e) => set("status", e.target.value)}
                            className={inputCls}
                        >
                            <option value="draft">Draft (not visible on the public site)</option>
                            <option value="published">Published (live now)</option>
                            <option value="scheduled">Scheduled (goes live at date/time below)</option>
                        </select>
                    </Field>
                    {form.status === "scheduled" && (
                        <Field label="Publish at (ISO date/time)" hint="e.g. 2026-08-15T09:00 — times are UTC">
                            <input
                                data-testid="art-publish-at"
                                type="datetime-local"
                                value={form.publish_at || ""}
                                onChange={(e) => set("publish_at", e.target.value)}
                                className={inputCls}
                            />
                        </Field>
                    )}
                </Section>

                <div className="flex flex-wrap items-center gap-4 pt-4 border-t border-[#E5E1DB]">
                    <button
                        type="button"
                        onClick={() => submit("draft")}
                        data-testid="art-save-draft"
                        disabled={saving}
                        className="px-6 py-3 border border-[#1A1A1A] text-[#1A1A1A] text-[11px] uppercase tracking-[0.22em] hover:bg-[#1A1A1A] hover:text-[#F9F8F6] disabled:opacity-50"
                    >
                        Save as draft
                    </button>
                    <button
                        type="button"
                        onClick={() => submit("published")}
                        data-testid="art-publish"
                        disabled={saving}
                        className="px-6 py-3 bg-[#1A1A1A] text-[#F9F8F6] text-[11px] uppercase tracking-[0.22em] hover:bg-black disabled:opacity-50"
                    >
                        Publish
                    </button>
                    <button
                        type="submit"
                        data-testid="art-save"
                        disabled={saving}
                        className="px-6 py-3 text-[11px] uppercase tracking-[0.22em] text-[#5C5855] hover:text-[#1A1A1A]"
                    >
                        {saving ? "Saving…" : "Save with current status"}
                    </button>
                    <Link to="/admin/articles" className="text-[11px] uppercase tracking-[0.22em] text-[#5C5855] hover:text-[#1A1A1A]">
                        Cancel
                    </Link>
                </div>
            </form>
        </main>
    );
}

const inputCls = "w-full bg-white border border-[#E5E1DB] focus:border-[#1A1A1A] outline-none px-4 py-3 text-sm text-[#1A1A1A]";

const Section = ({ title, children }) => (
    <section>
        <p className="asc-overline mb-4">{title}</p>
        <div className="space-y-5">{children}</div>
    </section>
);

const Field = ({ label, hint, required, children }) => (
    <label className="block">
        <span className="block text-[10px] uppercase tracking-[0.22em] text-[#8C7A6B] mb-2">
            {label}{required && <span className="text-red-700"> *</span>}
            {hint && <span className="ml-2 lowercase tracking-normal text-[#A89F94] normal-case">— {hint}</span>}
        </span>
        {children}
    </label>
);
